// Funções puras do "Direcionar" (convidar participantes). CommonJS (ver utils/messages.js).

const ROLE_OWNER = 'OWNER'
const ROLE_INVITED = 'INVITED'
const HISTORY_FROM_NOW = 'FROM_NOW'
const HISTORY_FULL = 'FULL_HISTORY'
const NOTE_MAX_LENGTH = 500

// Prazo do convite em dias; null = sem prazo.
const EXPIRY_OPTIONS = [
  { value: null, text: 'Sem prazo' },
  { value: 7, text: '7 dias' },
  { value: 30, text: '30 dias' },
  { value: 90, text: '90 dias' },
]

const TYPE_OPTIONS = [
  { value: null, text: 'Todos' },
  { value: 'planner', text: 'Planejadores' },
  { value: 'unit', text: 'Unidades de negócio' },
]

const participantsOf = conversation => (conversation && Array.isArray(conversation.participants)
  ? conversation.participants
  : [])

const invitedParticipants = conversation => participantsOf(conversation)
  .filter(participant => participant.role === ROLE_INVITED)

// Só o dono, com a conversa ativa e permissão de escrita, convida e remove.
const canManageParticipants = (conversation, canWrite) => !!conversation
  && conversation.myRole === ROLE_OWNER
  && conversation.status === 'ACTIVE'
  && !!canWrite

const myParticipant = (conversation, myPrincipal) => (myPrincipal
  ? participantsOf(conversation).find(participant => participant.principal === myPrincipal) || null
  : null)

const canLeave = (conversation, myPrincipal) => {
  const me = myParticipant(conversation, myPrincipal)
  return !!me && me.role === ROLE_INVITED && !!me.participantId
}

const expiresAtFromDays = (days, now = Date.now()) => (days
  ? new Date(now + Number(days) * 24 * 60 * 60 * 1000).toISOString()
  : undefined)

const buildInvitePayload = ({
  coreUserId, historyScope, note, expiryDays,
}, now = Date.now()) => {
  const payload = {
    coreUserId: Number(coreUserId),
    historyScope: historyScope === HISTORY_FULL ? HISTORY_FULL : HISTORY_FROM_NOW,
  }
  const trimmed = typeof note === 'string' ? note.trim() : ''
  if (trimmed) payload.note = trimmed.slice(0, NOTE_MAX_LENGTH)
  const expiresAt = expiresAtFromDays(expiryDays, now)
  if (expiresAt) payload.expiresAt = expiresAt
  return payload
}

// Parâmetros de GET /api/customer-chat/invitable-users (backend-core), sem vazios.
const invitableSearchParams = ({
  keyword, departmentId, type, customerId,
}, limit = 20) => {
  const params = { limit }
  const term = typeof keyword === 'string' ? keyword.trim() : ''
  if (term.length >= 2) params.keyword = term
  if (departmentId) params.department_id = departmentId
  if (type === 'planner' || type === 'unit') params.type = type
  if (customerId) params.customer_id = customerId
  return params
}

// Quem já participa não aparece no picker.
const filterInvitable = (users, conversation) => {
  const taken = participantsOf(conversation).map(participant => participant.principal)
  return (users || []).filter(user => !taken.includes(`core:${user.coreUserId}`))
}

const inviteErrorMessage = error => {
  const status = error && error.status
  if (status === 403) return 'Só o responsável pela conversa pode direcionar.'
  if (status === 409) {
    const apiMessage = error.apiErrors && error.apiErrors[0] && error.apiErrors[0].message
    return apiMessage && /at most/.test(apiMessage)
      ? 'A conversa já atingiu o limite de participantes convidados.'
      : 'Essa pessoa já participa da conversa.'
  }
  if (status === 422) return 'Essa pessoa não pode atender clientes pelo chat.'
  if (status === 503) return 'Não foi possível confirmar o usuário no Oráculo agora. Tente novamente em instantes.'
  return 'Não foi possível direcionar a conversa. Tente novamente.'
}

// Aplica participant.added / participant.removed na conversa (sem refetch).
const applyParticipantFrame = (conversation, type, participant) => {
  if (!conversation || !participant) return conversation
  const key = participant.participantId || participant.principal
  const others = participantsOf(conversation)
    .filter(existing => (existing.participantId || existing.principal) !== key)
  if (type === 'participant.added') return { ...conversation, participants: others.concat([participant]) }
  if (type === 'participant.removed') return { ...conversation, participants: others }
  return conversation
}

const historyScopeLabel = participant => {
  if (!participant || participant.role !== ROLE_INVITED) return ''
  return participant.historyScope === HISTORY_FULL ? 'Histórico completo' : 'A partir do convite'
}

module.exports = {
  ROLE_OWNER,
  ROLE_INVITED,
  HISTORY_FROM_NOW,
  HISTORY_FULL,
  NOTE_MAX_LENGTH,
  EXPIRY_OPTIONS,
  TYPE_OPTIONS,
  invitedParticipants,
  canManageParticipants,
  myParticipant,
  canLeave,
  expiresAtFromDays,
  buildInvitePayload,
  invitableSearchParams,
  filterInvitable,
  inviteErrorMessage,
  applyParticipantFrame,
  historyScopeLabel,
}
